import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Trash2, Loader2, MessageSquare, Plus } from "lucide-react";

const SOURCE_LABEL = { website: "Hemsidan", phone: "Telefon", sms: "SMS", instagram: "Instagram",
                       facebook: "Facebook", whatsapp: "WhatsApp", "walk-in": "På plats" };
const LOG_SOURCES = [["phone", "Telefon"], ["walk-in", "På plats"], ["instagram", "Instagram"],
                     ["facebook", "Facebook"], ["whatsapp", "WhatsApp"], ["sms", "SMS"]];
const EMPTY_LOG = { name: "", phone: "", source: "phone", message: "" };

/* ============ MESSAGES (customer contact, all channels) ============ */
export default function MessagesPanel({ onChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [logDialog, setLogDialog] = useState(false);
  const [logForm, setLogForm] = useState(EMPTY_LOG);
  const [logSaving, setLogSaving] = useState(false);

  const saveLog = async () => {
    if (!logForm.message.trim()) { toast.error("Skriv meddelandet"); return; }
    setLogSaving(true);
    try {
      await api.post("/admin/messages", logForm);
      toast.success("Meddelandet loggat");
      setLogDialog(false); setLogForm(EMPTY_LOG);
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
    finally { setLogSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/admin/messages"); setMessages(res.data.messages || []); }
    catch { toast.error("Kunde inte ladda meddelanden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMessage = async (m) => {
    setExpanded(e => (e === m.id ? null : m.id));
    if (!m.read) {
      try {
        await api.put(`/admin/messages/${m.id}/read`);
        setMessages(ms => ms.map(x => x.id === m.id ? { ...x, read: true } : x));
        onChange?.();
      } catch { /* */ }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort det här meddelandet?")) return;
    try { await api.delete(`/admin/messages/${id}`); setMessages(ms => ms.filter(x => x.id !== id)); onChange?.(); }
    catch { toast.error("Kunde inte ta bort"); }
  };

  const markAllRead = async () => {
    try { await api.put("/admin/messages/read-all"); setMessages(ms => ms.map(x => ({ ...x, read: true }))); onChange?.(); }
    catch { toast.error("Kunde inte markera"); }
  };

  const fmt = (s) => { try { return new Date(s).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } };
  const unread = messages.filter(m => !m.read).length;

  return (
    <div className="max-w-3xl" data-testid="messages-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Meddelanden</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Kundkontakt från alla kanaler – hemsidan, telefon, sociala medier.</p>
        </div>
        <div className="flex gap-2">
          {unread > 0 && (
            <Button onClick={markAllRead} variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]">
              <Check className="w-4 h-4 mr-2" /> Markera alla som lästa
            </Button>
          )}
          <Button onClick={() => { setLogForm(EMPTY_LOG); setLogDialog(true); }}
            className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Logga samtal/DM
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : messages.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <MessageSquare className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga meddelanden ännu.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map(m => (
            <div key={m.id} className={`bg-[#121212] rounded-xl border transition-all ${m.read ? "border-[#27272A]" : "border-[#D4B483]/50"}`}>
              <button onClick={() => openMessage(m)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.read ? "bg-transparent border border-[#3F3F46]" : "bg-[#D4B483]"}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${m.read ? "text-[#D4D4D8]" : "text-white font-medium"}`}>
                    {m.name || "Kund"}
                    {m.source && m.source !== "website" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#3B82F6]/12 text-[#93C5FD]">{SOURCE_LABEL[m.source] || m.source}</span>
                    )}
                  </p>
                  <p className="text-[11px] text-[#9A9AA2] truncate">{expanded === m.id ? (m.email || "") : m.message}</p>
                </div>
                <span className="text-[11px] text-[#9A9AA2] flex-shrink-0">{fmt(m.created_at)}</span>
              </button>
              {expanded === m.id && (
                <div className="px-4 pb-4 pt-1 border-t border-[#1F1F22]">
                  <p className="text-sm text-[#E8E8EA] whitespace-pre-wrap leading-relaxed mb-3">{m.message}</p>
                  <div className="flex items-center gap-3 flex-wrap text-xs">
                    {m.email && <a href={`mailto:${m.email}?subject=${encodeURIComponent("Svar från Bästa Biten")}`} className="text-[#D4B483] hover:text-[#E5CFA5] font-medium">Svara via mejl</a>}
                    {m.phone && <span className="text-[#9A9AA2]">{m.phone}</span>}
                    <span className="text-[#9A9AA2]">{m.location}</span>
                    <button onClick={() => remove(m.id)} className="ml-auto text-[#71717A] hover:text-[#EF4444] flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> Ta bort
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Log a phone call / DM manually */}
      <Dialog open={logDialog} onOpenChange={setLogDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md">
          <DialogHeader><DialogTitle className="text-white">Logga samtal / DM</DialogTitle></DialogHeader>
          <p className="text-xs text-[#C0C0C6]">Skriv in vad kunden sa — det hamnar i inkorgen och på kundens tidslinje (matchas via telefonnumret).</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kundens namn</Label>
              <Input value={logForm.name} onChange={e => setLogForm(f => ({ ...f, name: e.target.value }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
              <Input value={logForm.phone} onChange={e => setLogForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kanal</Label>
            <select value={logForm.source} onChange={e => setLogForm(f => ({ ...f, source: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-sm px-2">
              {LOG_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Meddelande *</Label>
            <Textarea value={logForm.message} onChange={e => setLogForm(f => ({ ...f, message: e.target.value }))}
              rows={3} className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
          </div>
          <Button onClick={saveLog} disabled={logSaving || !logForm.message.trim()}
            className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            {logSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Spara
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
